/**
 * The full-height landing hero: a concise promise, a working video-intent
 * composer, and one complete 9:16 result that makes the product legible at a
 * glance. This is the production implementation of the approved v5.4 review
 * surface; the composer and onboarding state machine remain unchanged.
 *
 * No dashboard, no metrics, no signal cards, no secondary CTA. The hero is
 * emotional rather than analytical — merchants meet an outcome first and the
 * machinery only once they are interested.
 *
 * The navigation remains mounted outside this element so its sticky behavior
 * is independent of the hero's clipped atmospheric background.
 */

import { useState } from 'react';

import { useReducedMotion } from '../hooks/useReducedMotion.js';
import { useLanguage } from '../i18n/language.js';
import type { ContextSource } from '../lib/classifyContextUrl.js';
import type { MerchantProfileView } from '../merchant/api.js';
import type { ComposerFlowPhase } from './ContextComposer.js';
import { HeroSignalField } from './HeroSignalField.js';
import { VideoIntentComposer, type ComposerIntent } from './VideoIntentComposer.js';

export function Hero({
  contextNote,
  onContextNoteChange,
  sources,
  onAddSource,
  onRemoveSource,
  onSubmit,
  phase,
  profileView,
  busy,
  intakeError,
  onConfirmProfile,
  onProfileIncorrect,
  onRetrySources,
  instagramBusinessEnabled = false,
  instagramReanalysisWarning = false,
}: {
  readonly contextNote: string;
  readonly onContextNoteChange: (value: string) => void;
  readonly sources: readonly ContextSource[];
  readonly onAddSource: (source: ContextSource) => void;
  readonly onRemoveSource: (id: string) => void;
  readonly onSubmit: (intent: ComposerIntent) => void;
  readonly phase: ComposerFlowPhase;
  readonly profileView: MerchantProfileView | null;
  readonly busy: boolean;
  readonly intakeError: string | null;
  readonly onConfirmProfile: () => void;
  readonly onProfileIncorrect: () => void;
  readonly onRetrySources: () => void;
  readonly instagramBusinessEnabled?: boolean;
  readonly instagramReanalysisWarning?: boolean;
}): React.ReactElement {
  const { copy } = useLanguage();
  const reducedMotion = useReducedMotion();
  const configuredHeroVideoUrl = import.meta.env['VITE_HERO_VIDEO_URL']?.trim();
  const [videoUnavailable, setVideoUnavailable] = useState(false);
  // The signals gather toward the composer as soon as the merchant gives it
  // something — a note or a source — before any processing starts.
  const attention = contextNote.trim().length > 0 || sources.length > 0;

  return (
    <section
      id="top"
      className="mj-landing-hero"
      data-landing-screen="1"
      data-composer-phase={phase}
      aria-label={copy.hero.landmark}
    >
      <HeroSignalField phase={phase} attention={attention} />

      <div className="mj-hero-content">
        <div className="mj-hero-copy">
          <p className="mj-hero-eyebrow">
            <span className="mj-hero-eyebrow-dot" aria-hidden="true" />
            {copy.hero.eyebrow}
          </p>
          <h1>{copy.hero.headline}</h1>
          <p className="mj-hero-support">{copy.hero.support}</p>
        </div>

        <div className="mj-composer-slot">
          <VideoIntentComposer
            contextNote={contextNote}
            onContextNoteChange={onContextNoteChange}
            sources={sources}
            onAddSource={onAddSource}
            onRemoveSource={onRemoveSource}
            onSubmit={onSubmit}
            phase={phase}
            profileView={profileView}
            busy={busy}
            externalError={intakeError}
            onConfirmProfile={onConfirmProfile}
            onProfileIncorrect={onProfileIncorrect}
            onRetrySources={onRetrySources}
            instagramBusinessEnabled={instagramBusinessEnabled}
            instagramReanalysisWarning={instagramReanalysisWarning}
          />
        </div>

        <figure className="mj-hero-video-stage" aria-label={copy.hero.videoLabel}>
          <div className="mj-hero-video-note mj-hero-context-note">
            <span>{copy.hero.startsWith}</span>
            <strong>{copy.hero.yourBusiness}</strong>
          </div>
          <div className="mj-hero-video-note mj-hero-channel-note">
            <span>{copy.hero.madeFor}</span>
            <strong>{copy.hero.channels}</strong>
          </div>

          <div className="mj-hero-video-card">
            {videoUnavailable || reducedMotion ? (
              <img
                className="mj-hero-video"
                src="/hero-video-poster.webp"
                alt=""
                aria-hidden="true"
              />
            ) : configuredHeroVideoUrl ? (
              <video
                className="mj-hero-video"
                src={configuredHeroVideoUrl}
                poster="/hero-video-poster.webp"
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                aria-hidden="true"
                onError={() => {
                  setVideoUnavailable(true);
                }}
              />
            ) : (
              <img
                className="mj-hero-video"
                src="/hero-video-poster.webp"
                alt=""
                aria-hidden="true"
                onError={() => {
                  setVideoUnavailable(true);
                }}
              />
            )}
            <div className="mj-hero-video-top" aria-hidden="true">
              <span className="mj-hero-live-dot" />
              <span>{copy.hero.generatedExample}</span>
              <span>9:16</span>
            </div>
            <figcaption className="mj-hero-video-caption">
              <strong>{copy.hero.videoTitle}</strong>
              <span>{copy.hero.videoMeta}</span>
            </figcaption>
          </div>
        </figure>
      </div>
    </section>
  );
}
